import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Row, Col, Alert, Spinner, Card, Badge } from 'react-bootstrap';
import AuthContext from '../../../contexts/authContext';
import CompanieContext from '../../../contexts/companieContext';
import { getAPI, putAPI } from '../../../apis/backend/apiNode';
import { useLogicDevis } from '../../Devis/hooks/useLogicDevis';
import DevisLignesManager from '../../Devis/components/DevisLignesManager';
import DevisTotauxConfig from '../../Devis/components/DevisTotauxConfig';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import showNotification from '../../../components/extras/showNotification';
import Icon from 'components/icon/Icon';

const CreateEditDevisModal = ({
	isOpen,
	setIsOpen,
	onSuccess,
	onClose,
	interventionClient,
	intervention,
	devisId, // New prop for editing existing devis
	devisData: initialDevisData, // Optional pre-loaded devis data
}) => {
	const { companie } = useContext(CompanieContext);
	const { userData } = useContext(AuthContext);
	const navigate = useNavigate();
	const { createDevis, updateDevis } = useLogicDevis();

	const isEditMode = !!devisId;
	const currentUserId = userData?.id;

	const [loading, setLoading] = useState(isEditMode);
	const [error, setError] = useState(null);
	const [products, setProducts] = useState([]);
	const [selectedClient, setSelectedClient] = useState(null);
	const [showValidationErrors, setShowValidationErrors] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [originalDevisData, setOriginalDevisData] = useState(null);
	const technicienIds = (intervention?.techniciens?.length > 0)
		? intervention.techniciens.map(t => t?._id || t?.id).filter(Boolean)
		: (intervention?.cars || [])
			.flatMap(car => car?.conducteurs || [])
			.map(c => {
				// Gère si technicien est un objet peuplé ou juste un ID
				const tech = c?.technicien;
				return (typeof tech === 'object') ? (tech?._id || tech?.id) : tech;
			})
			.filter(Boolean);


	// Form data state
	const [formData, setFormData] = useState({
		clientId: interventionClient?._id || '',
		companyId: intervention?.companyId?._id || intervention?.cars?.[0]?.companyId || companie?._id || '',
		dateValidite: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
		name: interventionClient ? `Devis pour ${interventionClient.nom}` : '',
		notesClient: '',
		termsAndConditions: '',
		paymentMethods: '',
		remiseType: 'Aucune',
		remiseValue: 0,
		account_tax: null,
		tva: 20,
		technicienIds: technicienIds,
	});

	const [lignes, setLignes] = useState([]);

	// Use refs to track mounted state and prevent infinite loops
	const isMountedRef = useRef(true);
	const hasLoadedRef = useRef(false);

	// Fetch clients function
	const fetchClients = useCallback(async (searchTerm = '', signal = null) => {
		try {
			const queryParams = new URLSearchParams({ limit: '20', page: '1' });
			if (searchTerm) queryParams.append('search', searchTerm);
			const result = await getAPI('store2000_client', `?${queryParams.toString()}`, signal);
			return result?.success ? result.clients || [] : [];
		} catch (error) {
			return [];
		}
	}, []);

	// Render client option
	const renderClientOption = useCallback(
		(client) => {
			const mainEmail = client.emails?.[0] || '';
			const mainPhone = client.telephones?.[0] || '';

			return (
				<div className='dropdown-option'>
					<div className='d-flex align-items-center justify-content-between'>
						<div className='flex-grow-1'>
							<div className='fw-medium text-dark'>{client.nom}</div>
							{mainEmail && (
								<small className='text-muted d-block mt-1'>
									<i className='fas fa-envelope me-1'></i>
									{mainEmail}
								</small>
							)}
							{mainPhone && (
								<small className='text-muted d-block'>
									<i className='fas fa-phone me-1'></i>
									{mainPhone}
								</small>
							)}
						</div>
						{interventionClient && client._id === interventionClient._id && (
							<Badge bg='success' className='ms-2'>
								Client de l'intervention
							</Badge>
						)}
					</div>
				</div>
			);
		},
		[interventionClient],
	);

	// Load dependencies ONCE when component mounts or modal opens
	const loadDependencies = useCallback(async () => {
		if (!isOpen || hasLoadedRef.current) return;

		hasLoadedRef.current = true;
		try {
			const productsRes = await getAPI('stock', '/produits?limit=1000');
			setProducts(productsRes?.data?.produits || productsRes?.produits || []);
		} catch (err) {
			console.error('Failed to load products:', err);
		}
	}, [isOpen]);

	// Load devis data for edit mode
	const loadDevisData = useCallback(async () => {
		if (!devisId || !isOpen) return;

		setLoading(true);
		setError(null);

		try {
			const response = await getAPI('devis', `/${devisId}`);
			const data = response.data?.devis || response.data || response.devis;

			if (response?.success && data) {
				setOriginalDevisData(data);

				// Set form data
				setFormData((prev) => ({
					...prev,
					clientId: data.clientId?._id || data.clientId || '',
					companyId: data.companyId?._id || data.companyId || '',
					dateValidite: data.dateValidite
						? new Date(data.dateValidite).toISOString().split('T')[0]
						: prev.dateValidite,
					name: data.name || '',
					notesClient: data.notesClient || '',
					termsAndConditions: data.termsAndConditions || '',
					paymentMethods: data.paymentMethods || '',
					remiseType: data.remiseType || 'Aucune',
					remiseValue: data.remiseValue || 0,
					account_tax: data.account_tax || null,
					tva: data.tva || 20,
					technicienIds: data.technicienIds || [],
				}));

				// Set lines
				setLignes(data.lignes || []);

				// Set selected client
				if (data.clientId && typeof data.clientId === 'object') {
					setSelectedClient(data.clientId);
				}
			} else {
				const errorMsg = response?.error || response?.message || 'Devis non trouvé';
				setError(errorMsg);
				showNotification('Erreur', errorMsg, 'danger');
			}
		} catch (err) {
			setError('Erreur lors du chargement: ' + err.message);
			showNotification('Erreur', 'Erreur lors du chargement du devis', 'danger');
		} finally {
			setLoading(false);
		}
	}, [devisId, isOpen]);

	// Main initialization effect - FIXED to prevent infinite loop
	useEffect(() => {
		if (isOpen) {
			// Reset loading flag when modal opens
			hasLoadedRef.current = false;

			loadDependencies();
			setError(null);
			setShowValidationErrors(false);
			loadDevisData();
		}

		return () => {
			// Cleanup when component unmounts or modal closes
			isMountedRef.current = false;
		};
	}, [isOpen, isEditMode, devisId, interventionClient, intervention, companie]);


	// Form validation
	const validateForm = useCallback(() => {
		const errors = {};

		// Only validate required fields
		if (!formData.clientId) errors.clientId = 'Le client est obligatoire';
		if (!formData.companyId) errors.companyId = 'La société est obligatoire';

		// Validate lines
		if (!lignes || lignes.length === 0) {
			errors.lignes = 'Aucun article ajouté';
		} else {
			lignes.forEach((ligne, index) => {
				if (!ligne.description?.trim())
					errors[`lignes[${index}].description`] = 'Description requise';
				if (!ligne.quantite || parseFloat(ligne.quantite) <= 0)
					errors[`lignes[${index}].quantite`] = 'Qté invalide';

				const prix = parseFloat(ligne.prixUnitaireHT) || 0;
				const isNoteOrService = ['note', 'service'].includes(ligne.type);

				if (ligne.prixUnitaireHT === null || ligne.prixUnitaireHT === undefined)
					errors[`lignes[${index}].prixUnitaireHT`] = 'Prix invalide';
				else if (prix < 0) errors[`lignes[${index}].prixUnitaireHT`] = 'Prix négatif';
				else if (!isNoteOrService && prix === 0)
					errors[`lignes[${index}].prixUnitaireHT`] = 'Prix 0 impossible';
			});
		}
		return errors;
	}, [formData, lignes]);

	// Handle form submission
	const handleSubmit = async () => {
		setShowValidationErrors(true);
		setIsSubmitting(true);

		const errors = validateForm();
		if (Object.keys(errors).length > 0) {
			setError('Veuillez corriger les erreurs indiquées.');
			showNotification(
				'Erreur',
				'Veuillez corriger les erreurs dans le formulaire',
				'warning',
			);
			setIsSubmitting(false);
			return;
		}

		setError(null);
		setLoading(true);

		try {
			const payload = {
				clientId: formData.clientId,
				companyId: formData.companyId,
				createdBy: currentUserId,
				dateValidite: formData.dateValidite,
				termsAndConditions: formData.termsAndConditions,
				paymentMethods: formData.paymentMethods,
				name: formData.name,
				notesClient: formData.notesClient,
				lignes: lignes.map((ligne, index) => {
					let nomLigne = ligne.nom || ligne.description || `Ligne ${index + 1}`;
					if (ligne.produitId?.name) nomLigne = ligne.produitId.name;
					else if (typeof ligne.produitId === 'string') {
						const product = products.find((p) => p._id === ligne.produitId);
						if (product?.name) nomLigne = product.name;
					}

					return {
						type: ligne.type,
						produitId: ligne.produitId?._id || ligne.produitId || null,
						description: ligne.description,
						quantite: parseFloat(ligne.quantite) || 0,
						prixUnitaireHT: parseFloat(ligne.prixUnitaireHT) || 0,
						tva: parseFloat(ligne.tva || formData.tva || 20),
						nom: nomLigne,
						...(ligne.sourceDemandePrix && {
							sourceDemandePrix: ligne.sourceDemandePrix,
							sourceReponse: ligne.sourceReponse,
							fournisseur: ligne.fournisseur,
							delaiLivraison: ligne.delaiLivraison,
						}),
					};
				}),
				remiseType: formData.remiseType,
				remiseValue: formData.remiseValue,
				account_tax: formData.account_tax,
				tva: formData.tva,
				technicienIds: technicienIds,
				motif: intervention?.motif,
				origin: 'technicien',
				interventionId: intervention?._id || intervention?.id
			};

			console.log(`📤 ${isEditMode ? 'Updating' : 'Creating'} devis with payload:`, payload);

			let result;
			if (isEditMode) {
				result = await updateDevis(devisId, payload);
			} else {
				result = await createDevis(payload);
			}

			if (result?.success) {
				const devis = result.data?.devis || result.data;

				console.log(`✅ Devis ${isEditMode ? 'updated' : 'created'} successfully:`, devis);

				setIsOpen(false);
				showNotification(
					'Succès',
					`Devis ${isEditMode ? 'mis à jour' : 'créé'} avec succès!`,
					'success',
				);

				if (onSuccess) {
					onSuccess(devis);
				} else {
					navigate(`/devis/${devis._id}`);
				}
			} else {
				throw new Error(
					result?.error ||
					`Erreur lors de ${isEditMode ? 'la mise à jour' : 'la création'} du devis`,
				);
			}
		} catch (err) {
			console.error(`❌ Error ${isEditMode ? 'updating' : 'creating'} devis:`, err);
			setError('Erreur: ' + err.message);
			showNotification(
				'Erreur',
				`Erreur lors de ${isEditMode ? 'la mise à jour' : 'la création'} du devis: ` +
				err.message,
				'danger',
			);
		} finally {
			setLoading(false);
			setIsSubmitting(false);
		}
	};

	const handleModalClose = () => {
		setIsOpen(false);
		if (onClose) {
			onClose();
		}
	};
	const handleLigneUpdate = async (lignes) => {
		setLignes(lignes);
		const updatedValues = { ...formData, lignes };
		await handleUpdateDevis(updatedValues);
	}
	const [syncStatus, setSyncStatus] = useState('saved');
	const saveTimeoutRef = useRef(null);

	const handleUpdateDevis = async (values) => {
		setSyncStatus('saving');
		console.log("*****", values)

		if (saveTimeoutRef.current) {
			clearTimeout(saveTimeoutRef.current);
		}

		saveTimeoutRef.current = setTimeout(async () => {

			try {
				console.log("payload")

				const payload = {
					name: values.name,
					lignes: values.lignes.map((ligne, index) => {
						let nomLigne = ligne.nom || ligne.description || `Ligne ${index + 1}`;

						// Resolve product name logic
						if (ligne.produitId && typeof ligne.produitId === 'object' && ligne.produitId.name) {
							nomLigne = ligne.produitId.name;
						} else if (ligne.produitId && typeof ligne.produitId === 'string') {
							const product = products.find((p) => p._id === ligne.produitId);
							if (product?.name) nomLigne = product.name;
						}

						return {
							type: ligne.type,
							produitId: ligne.produitId?._id || ligne.produitId || null,
							description: ligne.description,
							quantite: parseFloat(ligne.quantite) || 0,
							prixUnitaireHT: parseFloat(ligne.prixUnitaireHT) || 0,
							tva: parseFloat(ligne.tva || values.tva),
							nom: nomLigne,
						};
					}),
					remiseType: values.remiseType,
					remiseValue: values.remiseValue,
					account_tax: values.account_tax,
					tva: values.tva,
				};

				// 2. Execute API Call
				const result = await putAPI('devis', payload, `/${devisId}`);

				if (result?.success) {
					setSyncStatus('saved'); // "Sauvegardé"
					return result;
				} else {
					setSyncStatus('error'); // Will show "Non sauvegardé" or error
					return result;
				}
			} catch (err) {
				setSyncStatus('error');
				console.log("err", err)

				// throw err;
			}

		}, 1000);

	};

	const canSubmit =
		!isSubmitting && !!formData.clientId && !!formData.companyId && lignes.length > 0;

	const isEditable = useMemo(() => {
		if (!isEditMode) return true;
		if (!originalDevisData) return true;
		return originalDevisData.statut === 'brouillon' || originalDevisData.statut === 'envoyé';
	}, [isEditMode, originalDevisData]);

	const modalTitle = useMemo(() => {
		if (isEditMode) {
			return `Modifier Devis ${originalDevisData?.reference || devisId}`;
		}
		return interventionClient ? `Devis pour ${interventionClient.nom}` : 'Nouveau Devis';
	}, [isEditMode, originalDevisData, devisId, interventionClient]);

	// Loading state
	if (loading && isEditMode) {
		return (
			<Modal show={isOpen} onHide={handleModalClose} size='xl' centered>
				<Modal.Header closeButton>
					<Modal.Title>Chargement...</Modal.Title>
				</Modal.Header>
				<Modal.Body className='text-center py-5'>
					<Spinner animation='border' variant='primary' />
					<p className='mt-3'>Chargement du devis...</p>
				</Modal.Body>
			</Modal>
		);
	}

	return (
		<Modal
			show={isOpen}
			onHide={handleModalClose}
			size='xl'
			centered
			scrollable
			className='nv-3'>
			<Modal.Header closeButton className='border-bottom bg-light'>
				<Modal.Title className='d-flex align-items-center'>
					<i
						className={`fas ${isEditMode ? 'fa-edit' : 'fa-file-invoice-dollar'
							} text-primary me-2`}></i>
					{modalTitle}
					{isEditMode && originalDevisData?.statut && (
						<Badge
							bg={originalDevisData.statut === 'brouillon' ? 'secondary' : 'info'}
							className='ms-2'>
							{originalDevisData.statut}
						</Badge>
					)}
					<span className={`fs-6 fw-normal d-flex align-items-center ${syncStatus === 'error' ? 'text-danger' :
						syncStatus === 'saved' ? 'text-success' :
							syncStatus === 'unsaved' ? 'text-warning' :
								'text-primary'
						}`}>
						{syncStatus === 'saving' && (
							<>
								<span className="spinner-border spinner-border-sm me-2" role="status"></span>
								En cours...
							</>
						)}

						{syncStatus === 'saved' && (
							<>
								<Icon icon="CheckCircle" className="me-1" />
								Sauvegardé
							</>
						)}

						{syncStatus === 'unsaved' && (
							<>
								<Icon icon="Edit" className="me-1" />
								Non sauvegardé
							</>
						)}

						{syncStatus === 'error' && (
							<>
								<Icon icon="ExclamationTriangle" className="me-1" />
								Erreur de sauvegarde
							</>
						)}
					</span>
				</Modal.Title>
			</Modal.Header>

			<Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
				{!isEditable && isEditMode && (
					<Alert variant='warning' className='mb-4'>
						<i className='fas fa-exclamation-triangle me-2'></i>
						Ce devis n'est plus modifiable car son statut est "
						{originalDevisData?.statut}". Seuls les devis en statut "brouillon" peuvent
						être modifiés.
					</Alert>
				)}

				{error && (
					<Alert variant='danger' className='mb-4'>
						<i className='fas fa-exclamation-triangle me-2'></i>
						{error}
					</Alert>
				)}

				<div className='row g-4'>
					{/* Simplified: Only title field visible */}
					{isEditable && (
						<div className='col-12'>
							<Form.Group className='mb-4'>
								<Form.Label className='fw-semibold'>Titre du devis</Form.Label>
								<Form.Control
									value={formData.name}
									onChange={async (e) => {
										setFormData((prev) => ({ ...prev, name: e.target.value }))
										const updatedValues = { ...formData, name: e.target.value, lignes };
										await handleUpdateDevis(updatedValues);
									}
									}
									placeholder='Ex: Rénovation cuisine été 2024'
									className='border-0 bg-light p-3 rounded-3'
									disabled={loading}
								/>
							</Form.Group>
						</div>
					)}

					{/* Lines manager - only editable if devis is editable */}
					<div className='col-12'>
						<div className='d-flex justify-content-between align-items-center mb-3'>
							<h6 className='fw-bold text-dark mb-0'>Articles & Services</h6>
							<Badge bg='light' text='dark' className='px-3 py-2'>
								{lignes.length} article(s)
							</Badge>
						</div>

						<DevisLignesManager
							lignes={lignes}
							onLignesChange={handleLigneUpdate}
							loading={loading || !isEditable}
							products={products}
						/>

						{showValidationErrors && validateForm().lignes && (
							<Alert variant='warning' className='mt-2 py-2'>
								<i className='fas fa-exclamation-triangle me-2'></i>
								{validateForm().lignes}
							</Alert>
						)}
					</div>

					{/* Totals configuration */}
					<div className='col-12'>
						<Card className='border-primary mt-4'>
							<Card.Header className='bg-primary text-white py-3'>
								<h6 className='mb-0'>
									<i className='fas fa-calculator me-2'></i>
									Configuration des totaux
								</h6>
							</Card.Header>
							<Card.Body>
								<DevisTotauxConfig
									devis={formData}
									company={intervention?.companyId}
									onUpdateRemise={async (remiseData) => {
										setFormData((prev) => ({
											...prev,
											remiseType: remiseData.remiseType,
											remiseValue: remiseData.remiseValue,
										}));
										setSyncStatus('saving');
										await putAPI(
											'devis',
											{ remiseType: remiseData.remiseType, remiseValue: remiseData.remiseValue },
											`/${devisId}/remise`,
										);
										setSyncStatus('saved');
									}}
									onUpdateTVA={async (tvaData) => {
										setFormData((prev) => ({
											...prev,
											tva: tvaData.tva,
										}));
										setSyncStatus('saving');
										await putAPI(
											'devis',
											tvaData,
											`/${devisId}/tva`,
										);
										setSyncStatus('saved');
									}}
									loading={loading || !isEditable}
									lignes={lignes}
								/>
							</Card.Body>
						</Card>
					</div>

					{/* Additional information display for non-editable devis */}

					<div className='col-12'>
						<Card className='mb-4'>
							<Card.Header className='bg-light'>
								<h6 className='mb-0'>Informations du devis</h6>
							</Card.Header>
							<Card.Body>
								<div className='row small'>
									<div className='col-md-6'>
										<p>
											<strong>Client:</strong>{' '}
											{originalDevisData?.clientId?.nom || 'Non spécifié'}
										</p>
										<p>
											<strong>Société:</strong>{' '}
											{originalDevisData?.companyId?.name ||
												'Non spécifié'}
										</p>
									</div>
									<div className='col-md-6'>
										<p>
											<strong>Date de validité:</strong>{' '}
											{new Date(
												originalDevisData?.dateValidite,
											).toLocaleDateString('fr-FR')}
										</p>
										<p>
											<strong>Référence:</strong>{' '}
											{originalDevisData?.reference}
										</p>
									</div>
								</div>
							</Card.Body>
						</Card>
					</div>

				</div>
			</Modal.Body>

			<Modal.Footer className='border-top bg-light'>
				<div className='d-flex justify-content-between w-100 align-items-center'>
					<div className='text-muted small'>
						{isEditMode && originalDevisData && (
							<Badge bg='secondary' className='me-2'>
								{originalDevisData.reference}
							</Badge>
						)}
					</div>

					<div className='d-flex gap-2'>
						<Button
							variant='outline-secondary'
							onClick={handleModalClose}
							disabled={isSubmitting}>
							Annuler
						</Button>

						{isEditable && (
							<Button
								variant='primary'
								onClick={handleSubmit}
								disabled={!canSubmit || isSubmitting}
								className='px-4'>
								{isSubmitting ? (
									<>
										<Spinner size='sm' className='me-2' />
										{isEditMode ? 'Mise à jour...' : 'Création...'}
									</>
								) : (
									<>
										<i
											className={`fas ${isEditMode ? 'fa-save' : 'fa-check'
												} me-2`}></i>
										{isEditMode ? 'Mettre à jour' : 'Créer le devis'}
									</>
								)}
							</Button>
						)}
					</div>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

CreateEditDevisModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	setIsOpen: PropTypes.func.isRequired,
	onSuccess: PropTypes.func,
	onClose: PropTypes.func,
	interventionClient: PropTypes.object,
	intervention: PropTypes.object,
	devisId: PropTypes.string, // For edit mode
	devisData: PropTypes.object, // Optional pre-loaded data
};

export default CreateEditDevisModal;
